import React from "react";
import { observer, Observer } from "mobx-react";
import moment from "moment";
import locale from "@bonliva-crm/locale";
import { Form } from "@modules/formx-react";
import { toSEK } from "@utils/formatCurrency";
import { useSnackbars, useUploadRequest } from "@hooks";
import { useNavigation } from "@navigation";
import { Overlay, PrimaryButton, RegularButton, SpinnerOverlay, Topbar } from "@common";
import { BookingWeekCardQuery } from "@bookings/BookingWeekCard/BookingWeekCardQuery";
import { FormxDebugger, Text, Date, Checkbox } from "@common/formx";
import { FileList } from "@common/FileList/FileList";
import { WorkRequestPeriods } from "../../components/week-periods";
import { weeksToDurations } from "@bonliva-crm/common";
import { TravelConfirmationStore } from "./TravelConfirmationStore";
import {
  Card,
  DialogActions,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from "@material-ui/core";
import { DnDLocalContainer } from "../../../containers/DnDContainer/DnDLocalContainer";
import { FileUpload } from "mdi-material-ui";
import { FormSection } from "@common/forms";
import { ThemeType } from "@theme";
import { LegalPersonType } from "@bonliva-crm/graphql";

type Props = {
  isNew: boolean;
  formStore: TravelConfirmationStore;
};

export const TravelConfirmationForm = observer((props: Props) => {
  const {
    formStore: { form },
    formStore,
    isNew
  } = props;
  const { showSuccess, showError } = useSnackbars();
  const { requestUpload } = useUploadRequest(true);
  const nav = useNavigation();
  const classes = useStyles({});

  return (
    <Form store={form} allowUpdates>
      <Grid container spacing={2} style={{ padding: 16, marginTop: -16 }}>
        <FormxDebugger store={form} />

        <Grid item xs={12}>
          <Topbar>
            <Typography variant="h6">Ladda upp bokningsbekräftelse</Typography>
          </Topbar>
          <Divider style={{ margin: "8px -16px 0" }} />
        </Grid>

        <Grid item xs={12}>
          <BookingWeekCardQuery bookingId={formStore.bookingId} />
        </Grid>

        <Grid item xs={12}>
          <Paper style={{ padding: 16 }}>
            <div style={{ position: "relative" }}>
              <SpinnerOverlay
                show={form.isSubmitting}
                style={{ margin: "-16px", borderRadius: "4px 4px 0 0" }}
              />
              <FormSection
                flat
                fullWidth
                withBorder
                PaperProps={{ style: { padding: 0 } }}
              >
                <DnDLocalContainer
                  className={classes.withFileUpload}
                  overlayProps={{ className: classes.dndOverlay }}
                  onDrop={(files) => formStore.form.push("files", files)}
                  noClick
                >
                  <RegularButton
                    variant="text"
                    leftIcon={<FileUpload />}
                    onClick={() =>
                      requestUpload().then((files) => formStore.form.push("files", files))
                    }
                    style={{ margin: "8px auto" }}
                  >
                    {"Ladda upp fil"}
                  </RegularButton>
                  <div style={{ width: "100%", padding: "0 16px" }}>
                    <Typography variant="subtitle2">Filer att ladda upp:</Typography>
                    <FileList
                      files={formStore.form.values.files}
                      onDelete={(i) => formStore.form.removeByIndex("files", i)}
                    />
                  </div>
                </DnDLocalContainer>
              </FormSection>

              <Divider style={{ margin: "8px -16px" }} />
              {isNew && (
                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                  Välj veckor för vilken resan gäller:
                </Typography>
              )}

              <WorkRequestPeriods
                bookings={weeksToDurations(
                  formStore.bookingData.BookingGroup.bookings.map((x) => x.Week)
                )}
                disableNotBooked
                // disabled
                skipDisabledSpans
                onChange={(v) => formStore.setWeeks(v)}
                value={formStore.weeksValue}
              />

              <Divider style={{ margin: "8px -16px" }} />

              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Välj vecka att ta upp resa:
              </Typography>

              <WorkRequestPeriods
                bookings={weeksToDurations(
                  formStore.bookingData.BookingGroup.bookings.map((x) => x.Week)
                )}
                disableNotBooked
                // disabled
                skipDisabledSpans
                onChange={(v) => formStore.setTravelWeek(v)}
                value={formStore.travelWeekValue}
              />
            </div>

            <Divider style={{ margin: "8px -16px" }} />

            <Grid item xs={4}>
              <Text
                name={`totalCost`}
                label={locale.TotalCost}
                TextFieldProps={{
                  type: "number",
                  InputProps: {
                    endAdornment: locale.currency.SEK
                  }
                }}
              />
            </Grid>
            {formStore.bookingData?.Consultant?.LegalPersonType ===
              LegalPersonType.Subcontractor && (
              <Grid item xs={4}>
                <div>
                  <Checkbox
                    name={"totalExVAT"}
                    label={
                      <Typography
                        color={
                          formStore?.form?.getError("totalExVAT") ? "error" : "initial"
                        }
                      >
                        {locale.TotalExcludeVAT}?
                      </Typography>
                    }
                  />
                </div>
              </Grid>
            )}

            <Divider style={{ margin: "8px -16px" }} />

            <Grid item xs={12} style={{ padding: "0 16px" }}>
              <Grid container spacing={2} style={{ paddingTop: 16 }}>
                {formStore.form.values.weeks
                  .map((x, i) => ({ item: x, index: i }))
                  .filter((x) => x.item.isTravel)
                  .sortBy((x) => x.item.weekYear.Week + x.item.weekYear.Year * 53)
                  .map(({ item: { startDate, endDate, weekYear }, index }) => {
                    return (
                      <Grid item xs={6} md={4} xl={3} key={index}>
                        <Card style={{ padding: 16, position: "relative" }}>
                          <Overlay in={formStore.isWeekDisabled(weekYear)} />

                          <Typography>
                            Vecka {weekYear.Week}-{weekYear.Year}
                          </Typography>

                          <Text
                            name={`weeks.${index}.cost`}
                            label="Kostnad"
                            TextFieldProps={{
                              type: "number",
                              InputProps: {
                                endAdornment: <span>kr.</span>
                              }
                            }}
                          />

                          <Text
                            name={`weeks.${index}.description`}
                            label="Beskrivning av resa"
                            TextFieldProps={{
                              style: { marginTop: 8 }
                            }}
                          />

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between"
                            }}
                          >
                            <Date
                              name={`weeks.${index}.startDate`}
                              label={locale.FromTravel}
                              DatePickerProps={{
                                maxDate: endDate
                                  ? moment.utc(endDate).subtract(1, "day").toDate()
                                  : null
                              }}
                            />
                            <Date
                              name={`weeks.${index}.endDate`}
                              label={locale.ToTravel}
                              DatePickerProps={{
                                minDate: startDate
                                  ? moment.utc(startDate).add(1, "day").toDate()
                                  : null
                              }}
                            />
                          </div>
                          <Text
                            name={`weeks.${index}.note`}
                            label={locale.Notes}
                            TextFieldProps={{
                              style: { marginTop: 8 }
                            }}
                          />
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            </Grid>

            <Divider style={{ margin: "8px -16px" }} />

            <Grid item xs={4} style={{ paddingTop: 16, marginLeft: "auto" }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Total verklig kostnad:</TableCell>
                    <TableCell align="right">
                      {toSEK(formStore.form.values.totalCost)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
            <Divider style={{ margin: "8px -16px" }} />

            <Observer>
              {() => (
                <DialogActions style={{ padding: "16px 0" }}>
                  <RegularButton
                    style={{ marginLeft: "auto" }}
                    onClick={() => nav.close()}
                  >
                    {locale.Cancel}
                  </RegularButton>

                  <PrimaryButton
                    disabled={!form.canSubmit}
                    loading={form.isSubmitting}
                    onClick={() => {
                      formStore
                        .submit()
                        .then(() => {
                          showSuccess(locale.Saved);
                          nav.unBlock("trav_conf");
                          nav.close();
                        })
                        .catch((err) =>
                          showError(`Cannot save\nReason: ${err.toString()}`)
                        );
                    }}
                  >
                    {locale.Send}
                  </PrimaryButton>
                </DialogActions>
              )}
            </Observer>
          </Paper>
        </Grid>
      </Grid>
    </Form>
  );
});

const useStyles = makeStyles((theme: ThemeType) => ({
  dndOverlay: { borderRadius: 4 },

  withFileUpload: {
    // borderStyle: "dashed",
    display: "flex",
    flex: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    // padding: 16,
    paddingTop: 0,
    paddingBottom: 0
  }
}));
